import React from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer'
import demoVideo from '../assets/demo-video.mp4'
import speedDemo from '../assets/speed-demo.mp4'
import pedDemo from '../assets/ped-demo.mp4'
import waitTimeDemo from '../assets/wait-time-demo.mp4'
import wirelessDiagram from '../assets/optimized/wireless-and-controllers-diagram.webp'
import ReactGA from 'react-ga4'
import { useEffect, lazy, Suspense } from 'react'
import ScrollArrow from '../components/ScrollArrow'
import { motion, useScroll, useTransform } from 'framer-motion'

const Home = () => {
  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: window.location.pathname })
  }, [])

  // Add scroll progress tracking for each video panel
  const { scrollYProgress } = useScroll()

  // Create refs for each panel to track their positions
  const panel2Ref = React.useRef(null)
  const panel3Ref = React.useRef(null)
  const panel5Ref = React.useRef(null)
  const panel6Ref = React.useRef(null)
  const panel7Ref = React.useRef(null)

  // Create individual scroll trackers for each panel
  const { scrollYProgress: panel2Progress } = useScroll({
    target: panel2Ref,
    offset: ['start end', 'end start']
  })

  const { scrollYProgress: panel3Progress } = useScroll({
    target: panel3Ref,
    offset: ['start end', 'end start']
  })

  const { scrollYProgress: panel5Progress } = useScroll({
    target: panel5Ref,
    offset: ['start end', 'end start']
  })

  const { scrollYProgress: panel6Progress } = useScroll({
    target: panel6Ref,
    offset: ['start end', 'end start']
  })

  const { scrollYProgress: panel7Progress } = useScroll({
    target: panel7Ref,
    offset: ['start end', 'end start']
  })

  // Transform scroll progress into scale values
  const panel2Scale = useTransform(panel2Progress, [0, 0.5, 1], [0.8, 1, 0.8])
  const panel3Scale = useTransform(panel3Progress, [0, 0.5, 1], [0.8, 1, 0.8])
  const panel5Scale = useTransform(panel5Progress, [0, 0.5, 1], [0.8, 1, 0.8])
  const panel6Scale = useTransform(panel6Progress, [0, 0.5, 1], [0.8, 1, 0.8])
  const panel7Scale = useTransform(panel7Progress, [0, 0.5, 1], [0.8, 1, 0.8])

  return (
    <div className='page'>
      <div className='panel panel1' id='panel1'>
        <div className='overlay'></div>
        <div className='video-background' style={{ overflow: 'hidden' }}>
          <video
            autoPlay
            muted
            loop
            playsInline
            fetchpriority='high'
            style={{
              position: 'absolute',
              height: '100%',
              width: '100%',
              right: '0',
              objectFit: 'cover',
              objectPosition: 'right center'
            }}
          >
            <source src={demoVideo} type='video/mp4' />
            Your browser does not support the video tag.
          </video>
        </div>
        <Header />
        <div className='first-panel-content'>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: false }}
            style={{ textAlign: 'left' }}
          >
            People hate traffic lights.
            <br />
            What if they didn't?
          </motion.h1>
          <motion.div
            style={{ marginTop: '40px' }}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: false }}
          >
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <div style={{ display: 'inline-block', textAlign: 'left' }}>
                {[
                  'Optimal signal timing that adapts to real-time conditions',
                  'No complex configurations or outdated timing plans',
                  'Instant responses to unsafe situations'
                ].map((text, index) => (
                  <motion.p
                    key={index}
                    style={{
                      fontSize: '18px',
                      marginBottom: '12px',
                      lineHeight: '1.4',
                      position: 'relative',
                      paddingLeft: '20px'
                    }}
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 * (index + 1) }}
                    viewport={{ once: false }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        left: '0',
                        top: '0',
                        fontSize: '18px',
                        lineHeight: '1.4'
                      }}
                    >
                      •
                    </span>
                    {text}
                  </motion.p>
                ))}
              </div>
            </div>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <motion.a
                className='book-demo-btn'
                target='_blank'
                href='https://calendar.app.google/dcayhCCPC9V9oHT19'
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                viewport={{ once: false }}
              >
                Schedule a Demo
              </motion.a>
            </div>
          </motion.div>
        </div>
        <ScrollArrow targetPanel='panel5' />
      </div>

      <div
        className='panel panel5 shapy-background'
        id='panel5'
        ref={panel5Ref}
      >
        <div>
          <h2 className='my-2'>Roundabout sees the world the way you do.</h2>
        </div>
        <motion.div
          style={{ scale: panel5Scale }}
          transition={{ duration: 0.3 }}
        >
          <video
            className='demo-video'
            autoPlay
            muted
            playsInline
            loading='lazy'
            preload='none'
            loop
            width='640'
            height='360'
          >
            <source src={waitTimeDemo} type='video/mp4' />
            Unable to play demo video
          </video>
        </motion.div>
        <ScrollArrow targetPanel='panel6' />
      </div>

      <div
        className='panel panel6 shapy-background'
        id='panel6'
        ref={panel6Ref}
      >
        <div>
          <h2 className='my-2'>
            Discourage speeding by giving red lights to speeding vehicles
          </h2>
        </div>
        <motion.div
          style={{ scale: panel6Scale }}
          transition={{ duration: 0.3 }}
        >
          <video
            className='demo-video'
            autoPlay
            muted
            playsInline
            loading='lazy'
            preload='none'
            loop
            width='640'
            height='360'
          >
            <source src={speedDemo} type='video/mp4' />
            Unable to play demo video
          </video>
        </motion.div>
        <ScrollArrow targetPanel='panel7' />
      </div>

      <div
        className='panel panel7 shapy-background'
        id='panel7'
        ref={panel7Ref}
      >
        <div>
          <h2 className='my-2'>
            Extend crossing times for pedestrians who need extra time
          </h2>
        </div>
        <motion.div
          style={{ scale: panel7Scale }}
          transition={{ duration: 0.3 }}
        >
          <video
            className='demo-video'
            autoPlay
            muted
            playsInline
            loading='lazy'
            preload='none'
            loop
            width='640'
            height='360'
          >
            <source src={pedDemo} type='video/mp4' />
            Unable to play demo video
          </video>
        </motion.div>
        <ScrollArrow targetPanel='panel8' />
      </div>

      <div className='panel panel8 bullet-point-panel-background' id='panel8'>
        <div className='overlay'></div>
        <div className='flex-content'>
          <motion.div
            style={{ maxWidth: '800px', marginBottom: '80px' }}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: false }}
          >
            <h1 style={{ textAlign: 'left', marginBottom: '50px' }}>
              Roundabout
              <br />
              isn't like
              <br />
              other systems.
            </h1>
          </motion.div>
          <motion.div
            style={{
              maxWidth: '800px',
              paddingBottom: '200px'
            }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: false }}
          >
            <ul className='custom-list mt-12' style={{ textAlign: 'left' }}>
              {[
                'Adapts to signals controlled by other agencies',
                'Responds automatically to special events, weather, and road closures',
                'Maintains perfect coordination that never falls out of sync',
                'Protects cyclists and pedestrians with all-red safety phases',
                'Reduces cumulative wait times by adapting to real-time traffic conditions',
                'Prioritizes buses and emergency vehicles without additional hardware'
              ].map((text, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 * (index + 1) }}
                  viewport={{ once: false }}
                >
                  {text}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
        <ScrollArrow targetPanel='panel9' />
      </div>

      <div
        className='panel panel9 shapy-background'
        id='panel9'
        ref={panel3Ref}
      >
        <div className='flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16 max-w-7xl mx-auto px-4'>
          <motion.div
            className='flex-1 max-w-2xl'
            style={{ scale: panel3Scale }}
            transition={{ duration: 0.3 }}
          >
            <img
              className='diagram-image w-full h-auto'
              src={wirelessDiagram}
              alt='Wireless and controllers diagram showing how Roundabout works'
              style={{ maxHeight: '70vh', objectFit: 'contain' }}
              width='800'
              height='600'
              loading='lazy'
            />
          </motion.div>
          <motion.div
            className='flex-1 max-w-2xl text-left'
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: false }}
          >
            <h2 className='text-3xl lg:text-4xl mb-8 text-white font-bold'>
              How Roundabout Works
            </h2>
            <div className='space-y-6'>
              <div className='bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10'>
                <h3 className='text-xl lg:text-2xl font-semibold text-white mb-3 flex items-center'>
                  <span className='bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                    1
                  </span>
                  Real-Time Vision System
                </h3>
                <p className='text-lg lg:text-xl leading-relaxed text-gray-200'>
                  The Roundabout Brain uses cameras to create a comprehensive,
                  live map of all road users—vehicles, pedestrians, cyclists,
                  buses, and emergency vehicles—updating 10 times per second for
                  complete situational awareness.
                </p>
              </div>

              <div className='bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10'>
                <h3 className='text-xl lg:text-2xl font-semibold text-white mb-3 flex items-center'>
                  <span className='bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                    2
                  </span>
                  Intelligent Decision Making
                </h3>
                <p className='text-lg lg:text-xl leading-relaxed text-gray-200'>
                  Using this complete world model, our AI makes instant
                  decisions to optimize both safety and traffic flow and
                  communicates them to existing traffic signal controllers.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
        <ScrollArrow targetPanel='panel10' />
      </div>

      <div
        className='panel panel10 shapy-background relative h-screen'
        id='panel10'
      >
        <motion.div
          className='flex flex-col items-center justify-center h-[calc(100%-160px)]'
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: false }}
        >
          <h1 className='text-center mb-8'>
            Ready for safer and more efficient traffic lights?
          </h1>
          <motion.a
            className='book-demo-btn mt-8'
            href='https://calendar.app.google/dcayhCCPC9V9oHT19'
            target='_blank'
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Schedule a Demo
          </motion.a>
        </motion.div>
        <Footer className='absolute bottom-0 w-full' />
      </div>
    </div>
  )
}

export default Home
